import React, { useState, useEffect, useRef } from "react";
import {
    Box, Typography, Card, Button, TextField, Select, MenuItem,
    FormControl, InputLabel, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Dialog, DialogTitle, DialogContent, DialogActions,
    Avatar, Chip, IconButton, Tooltip, Grid, Alert, Tabs, Tab, Snackbar, Pagination,
    CircularProgress, Divider, Stack, InputAdornment
} from "@mui/material";
import {
    Add as AddIcon, Label as LabelIcon, Category as CategoryIcon,
    Edit as EditIcon, Delete as DeleteIcon, CheckCircle as ActiveIcon,
    Cancel as InactiveIcon, FilterAlt as FilterAltIcon, Close as CloseIcon,
    Search as SearchIcon, FileUpload as BulkActionIcon, Download as DownloadIcon
} from "@mui/icons-material";
import { useAuth } from "../contexts/AuthContext";
import masterDataService from "../services/masterDataService";
import { formatToUIDateTimeLong } from "../utils/dateUtils";


const PRIMARY = "#0077B6";
const TEXT_MAIN = "#1A2B45";
const TEXT_SUB = "#5C6F85";
const CARD_BG = "#FFFFFF";
const BORDER = "#E2E8F0";

const StatusChip = ({ status }: { status: "Active" | "Inactive" }) => (
    <Chip
        icon={status === "Active" ? <ActiveIcon sx={{ fontSize: "13px !important" }} /> : <InactiveIcon sx={{ fontSize: "13px !important" }} />}
        label={status}
        size="small"
        sx={{
            bgcolor: status === "Active" ? "#DCFCE7" : "#F1F5F9",
            color: status === "Active" ? "#16A34A" : "#6B7280",
            fontWeight: 700, fontSize: "0.71rem", borderRadius: "8px",
            border: `1px solid ${status === "Active" ? "#16A34A" : "#CBD5E1"}25`,
            "& .MuiChip-icon": { color: status === "Active" ? "#16A34A" : "#6B7280" },
        }}
    />
);

const SectionCard = ({ title, subtitle, icon, iconColor, iconBg, children, action }: {
    title: string; subtitle?: string; icon: React.ReactNode;
    iconColor: string; iconBg: string; children: React.ReactNode; action?: React.ReactNode;
}) => (
    <Card elevation={0} sx={{
        borderRadius: "20px", border: `1px solid ${BORDER}`, bgcolor: CARD_BG,
        overflow: "hidden", mb: 3, transition: "box-shadow 0.2s",
        "&:hover": { boxShadow: "0 8px 32px rgba(0,119,182,0.08)" },
    }}>
        <Box sx={{
            px: 3, py: 2.5, display: "flex", alignItems: "center", gap: 2,
            borderBottom: `1px solid ${BORDER}`, bgcolor: "#FAFBFC",
            justifyContent: "space-between", flexWrap: { xs: "wrap", md: "nowrap" },
        }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Avatar sx={{ bgcolor: iconBg, color: iconColor, width: 42, height: 42, borderRadius: "12px" }}>{icon}</Avatar>
                <Box>
                    <Typography fontWeight={800} sx={{ color: TEXT_MAIN, fontSize: "0.97rem", lineHeight: 1.2, whiteSpace: 'nowrap' }}>{title}</Typography>
                    {subtitle && <Typography variant="caption" sx={{ color: TEXT_SUB, whiteSpace: 'nowrap' }}>{subtitle}</Typography>}
                </Box>
            </Box>
            {action}
        </Box>
        <Box sx={{ p: 3 }}>{children}</Box>
    </Card>
);

type TagCategory = { id: string; name: string; description: string; status: "Active" | "Inactive" };
type TagData = { id: string; categoryId: string; categoryName: string; value: string; status: "Active" | "Inactive" };
interface LookupItem {
    id: string;
    name: string;
    code: string;
    description: string;
    scope: string;
    status: 'Active' | 'Inactive';
    values: { order: number; value: string; description: string; id?: string }[];
    createdAt?: string;
    updatedAt?: string;
}

interface MasterDataTabProps {
    readOnly?: boolean;
}

const MasterDataTab: React.FC<MasterDataTabProps> = ({ readOnly }) => {
    const { user } = useAuth();
    const activeTabValue = localStorage.getItem("masterDataTab") as "categories" | "tags" | "lookups" || "categories";
    const [activeTab, setActiveTab] = useState<"categories" | "tags" | "lookups">(activeTabValue);

    const [categories, setCategories] = useState<TagCategory[]>([]);
    const [allCategories, setAllCategories] = useState<TagCategory[]>([]);
    const [tags, setTags] = useState<TagData[]>([]);
    const [loadingCats, setLoadingCats] = useState(true);
    const [loadingTags, setLoadingTags] = useState(false);
    const [loadingLookups, setLoadingLookups] = useState(false);

    // Filter States
    const [catSearchQuery, setCatSearchQuery] = useState("");
    const [catStatusQuery, setCatStatusQuery] = useState("All");

    // UI States
    const [openCatDialog, setOpenCatDialog] = useState(false);
    const [openTagDialog, setOpenTagDialog] = useState(false);
    const [editingCatId, setEditingCatId] = useState<string | null>(null);
    const [editingTagId, setEditingTagId] = useState<string | null>(null);
    const [deleteCatId, setDeleteCatId] = useState<string | null>(null);
    const [deleteTagId, setDeleteTagId] = useState<string | null>(null);
    const [deleteLookupId, setDeleteLookupId] = useState<string | null>(null);
    const [snack, setSnack] = useState({ open: false, msg: "", sev: "success" as "success" | "error" });

    // Forms
    const [catForm, setCatForm] = useState({ name: "", description: "", status: "Active" as "Active" | "Inactive" });
    const [tagForm, setTagForm] = useState({ categoryId: "", value: "", status: "Active" as "Active" | "Inactive" });

    // Tag Search States
    const [tagSearchQuery, setTagSearchQuery] = useState("");
    const [tagStatusQuery, setTagStatusQuery] = useState("All");

    // Lookup States
    const [items, setItems] = useState<LookupItem[]>([]);
    const [lookupSearchQuery, setLookupSearchQuery] = useState("");
    const [openLookupDialog, setOpenLookupDialog] = useState(false);
    const [editingLookupId, setEditingLookupId] = useState<string | null>(null);
    const [lookupForm, setLookupForm] = useState<Omit<LookupItem, "id" | "status">>({
        name: "",
        code: "",
        description: "",
        scope: "",
        values: []
    });
    const [newLookupValue, setNewLookupValue] = useState({ order: 1, value: '', description: '' });
    const [showLookupValueInput, setShowLookupValueInput] = useState(false);
    const appendInputRef = useRef<HTMLInputElement>(null);

    // Bulk Action States
    const [openBulkDialog, setOpenBulkDialog] = useState(false);
    const [bulkTab, setBulkTab] = useState(0);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [fileId, setFileId] = useState<string | null>(null);
    const [uploadStatus, setUploadStatus] = useState<'idle' | 'uploaded' | 'completed' | 'error'>('idle');
    const [uploadResult, setUploadResult] = useState<{ totalRows: number; successCount: number; errorCount: number; errorMessages?: string[] } | null>(null);
    const [isBulkLoading, setIsBulkLoading] = useState(false);
    const [isDownloadingData, setIsDownloadingData] = useState(false);
    const [recentUploads, setRecentUploads] = useState<any[]>([]);

    // Pagination
    const [catPage, setCatPage] = useState(1);
    const [tagPage, setTagPage] = useState(1);
    const [lookupPage, setLookupPage] = useState(1);
    const rowsPerPage = 10;

    const totalCatPages = Math.ceil(categories.length / rowsPerPage);
    const displayedCats = categories.slice((catPage - 1) * rowsPerPage, catPage * rowsPerPage);

    const totalTagPages = Math.ceil(tags.length / rowsPerPage);
    const displayedTags = tags.slice((tagPage - 1) * rowsPerPage, tagPage * rowsPerPage);

    const totalLookupPages = Math.ceil(items.length / rowsPerPage);
    const displayedLookups = items.slice((lookupPage - 1) * rowsPerPage, lookupPage * rowsPerPage);

    useEffect(() => {
        const loadInitialData = async () => {
            if (allCategories.length > 0) return;
            setLoadingCats(true);
            try {
                const data = await masterDataService.getAllTagCategories();
                const mapped: TagCategory[] = data.map(item => ({
                    id: item.id,
                    name: item.name,
                    description: item.description || "",
                    status: item.isActive ? "Active" : "Inactive"
                }));
                setAllCategories(mapped);
                setCategories(mapped); // Initial view matches all categories
            } catch (err) {
                console.error("Failed to load initial tag categories:", err);
            } finally {
                setLoadingCats(false);
            }
        };

        if (activeTab === "categories" || activeTab === "tags") {
            loadInitialData();
        }
    }, [activeTab, allCategories.length]);

    useEffect(() => {
        if (activeTab !== "categories") return;
        // If search and filter are cleared, revert to the full list (which was fetched initially)
        if (!catSearchQuery && catStatusQuery === "All") {
            setCategories(allCategories);
            return;
        }

        const fetchFilteredCategories = async () => {
            setLoadingCats(true);
            try {
                const data = await masterDataService.searchTagCategories(catSearchQuery, catStatusQuery);
                const mapped: TagCategory[] = data.map(item => ({
                    id: item.id,
                    name: item.name,
                    description: item.description || "",
                    status: item.isActive ? "Active" : "Inactive"
                }));
                setCategories(mapped);
                setCatPage(1);
            } catch (err) {
                console.error("Failed to load filtered tag categories:", err);
            } finally {
                setLoadingCats(false);
            }
        };
        const timer = setTimeout(() => {
            fetchFilteredCategories();
        }, 500);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab, catSearchQuery, catStatusQuery, allCategories]);

    useEffect(() => {
        if (activeTab !== "tags") return;

        const fetchFilteredTags = async () => {
            if (allCategories.length === 0) return; // wait until categories are loaded
            setLoadingTags(true);
            try {
                const mapItem = (item: any): TagData => {
                    const currentCat = allCategories.find(c => c.id === item.categoryId);
                    return {
                        id: item.id,
                        categoryId: item.categoryId,
                        categoryName: currentCat?.name || item.categoryName || "Unknown",
                        value: item.fieldValue,
                        status: item.isActive ? "Active" : "Inactive"
                    };
                };

                let merged: TagData[];

                if (!tagSearchQuery) {
                    // No text query — fetch all (status filter still applied via searchTags)
                    const data = await masterDataService.searchTags(undefined, undefined, tagStatusQuery);
                    merged = data.map(mapItem);
                } else {
                    // Search By tagName (category name) OR by tag value — make two parallel calls
                    const [byCat, byVal] = await Promise.all([
                        masterDataService.searchTags(tagSearchQuery, undefined, tagStatusQuery),
                        masterDataService.searchTags(undefined, tagSearchQuery, tagStatusQuery),
                    ]);
                    // Deduplicate by id
                    const seen = new Set<string>();
                    merged = [];
                    for (const item of [...byCat, ...byVal]) {
                        if (!seen.has(item.id)) {
                            seen.add(item.id);
                            merged.push(mapItem(item));
                        }
                    }
                }

                setTags(merged);
                setTagPage(1);
            } catch (err) {
                console.error("Failed to search tags:", err);
            } finally {
                setLoadingTags(false);
            }
        };
        const timer = setTimeout(() => {
            fetchFilteredTags();
        }, 500);
        return () => clearTimeout(timer);
    }, [activeTab, allCategories, tagSearchQuery, tagStatusQuery]);

    // Lookups effect handles both initial fetch and search with debounce
    useEffect(() => {
        if (activeTab !== 'lookups') return;

        if (!lookupSearchQuery) {
            fetchLookups();
        } else {
            const timer = setTimeout(() => {
                handleSearchLookups(lookupSearchQuery);
            }, 500);
            return () => clearTimeout(timer);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab, lookupSearchQuery]);

    useEffect(() => {
        localStorage.setItem("masterDataTab", activeTab);
    }, [activeTab]);

    const handleSearchLookups = async (query: string) => {
        if (!query) {
            fetchLookups();
            return;
        }
        setLoadingLookups(true);
        try {
            const data = await masterDataService.searchLookups(query);
            const mapped: LookupItem[] = data.map(item => ({
                id: item.id,
                name: item.categoryName,
                code: item.categoryName,
                description: item.description || "",
                scope: "System",
                status: item.active ? "Active" : "Inactive",
                values: (item.lookupValues || []).map((v: any) => ({
                    id: v.id,
                    order: v.orderNumber || v.order || 0,
                    value: v.valueName,
                    description: v.description
                })),
                createdAt: item.createdAt,
                updatedAt: item.updatedAt
            }));
            setItems(mapped);
        } catch (err) {
            console.error("Failed to search lookups:", err);
        } finally {
            setLoadingLookups(false);
        }
    };


    const fetchLookups = async () => {
        setLoadingLookups(true);
        try {
            const data = await masterDataService.getAllLookups();

            const mapped: LookupItem[] = data.map(item => ({
                id: item.id,
                name: item.categoryName,
                code: item.categoryName,
                description: item.description || "",
                scope: "System",
                status: item.active ? "Active" : "Inactive",
                values: (item.lookupValues || []).map((v: any) => ({
                    id: v.id,
                    order: v.orderNumber || v.order || 0,
                    value: v.valueName,
                    description: v.description
                })),
                createdAt: item.createdAt,
                updatedAt: item.updatedAt
            }));
            setItems(mapped);
        } catch (err) {
            console.error("Failed to load lookups:", err);
        } finally {
            setLoadingLookups(false);
        }
    };

    const handleSaveCategory = async () => {
        if (!catForm.name) return;

        try {
            if (editingCatId) {
                const result = await masterDataService.updateTagCategory(editingCatId, {
                    name: catForm.name,
                    description: catForm.description,
                    isActive: catForm.status === "Active"
                });

                const updatedCat: TagCategory = {
                    id: result.id,
                    name: result.name,
                    description: result.description || "",
                    status: result.isActive ? "Active" : "Inactive"
                };
                setCategories(categories.map(c => c.id === editingCatId ? updatedCat : c));
                setAllCategories(prev => prev.map(c => c.id === editingCatId ? updatedCat : c));
                setSnack({ open: true, msg: "Category successfully updated!", sev: "success" });
            } else {
                const result = await masterDataService.createTagCategory({
                    name: catForm.name,
                    description: catForm.description,
                    isActive: catForm.status === "Active"
                });

                const newCat: TagCategory = {
                    id: result.id,
                    name: result.name,
                    description: result.description || "",
                    status: result.isActive ? "Active" : "Inactive"
                };
                setCategories([newCat, ...categories]);
                setAllCategories(prev => [newCat, ...prev]);
                setSnack({ open: true, msg: "Category successfully added!", sev: "success" });
            }
            setCatForm({ name: "", description: "", status: "Active" });
            setOpenCatDialog(false);
            setEditingCatId(null);
        } catch (error) {
            console.error("Failed to save tag category:", error);
            setSnack({ open: true, msg: "Failed to save category.", sev: "error" });
        }
    };

    const handleDeleteCategory = async () => {
        if (!deleteCatId) return;
        try {
            await masterDataService.deleteTagCategory(deleteCatId);
            setCategories(categories.filter(c => c.id !== deleteCatId));
            setAllCategories(prev => prev.filter(c => c.id !== deleteCatId));
            setDeleteCatId(null);
            setSnack({ open: true, msg: "Category successfully deleted!", sev: "success" });
        } catch (error) {
            console.error("Failed to delete tag category:", error);
            setSnack({ open: true, msg: "Failed to delete category.", sev: "error" });
        }
    };

    const handleDeleteTag = async () => {
        if (!deleteTagId) return;
        try {
            await masterDataService.deleteTag(deleteTagId);
            setTags(tags.filter(t => t.id !== deleteTagId));
            setDeleteTagId(null);
            setSnack({ open: true, msg: "Tag successfully deleted!", sev: "success" });
        } catch (error) {
            console.error("Failed to delete tag:", error);
            setSnack({ open: true, msg: "Failed to delete tag.", sev: "error" });
        }
    };

    const handleSaveTag = async () => {
        if (!tagForm.categoryId || !tagForm.value) return;

        try {
            if (editingTagId) {
                const result = await masterDataService.updateTag(editingTagId, {
                    categoryId: tagForm.categoryId,
                    fieldValue: tagForm.value,
                    isActive: tagForm.status === "Active"
                });

                const currentCat = categories.find(c => c.id === result.categoryId);
                const updatedTag: TagData = {
                    id: result.id,
                    categoryId: result.categoryId,
                    categoryName: currentCat?.name || "Unknown",
                    value: result.fieldValue,
                    status: result.isActive ? "Active" : "Inactive"
                };
                setTags(tags.map(t => t.id === editingTagId ? updatedTag : t));
                setSnack({ open: true, msg: "Tag successfully updated!", sev: "success" });
            } else {
                const result = await masterDataService.createTag({
                    categoryId: tagForm.categoryId,
                    fieldValue: tagForm.value,
                    isActive: tagForm.status === "Active"
                });

                const currentCat = categories.find(c => c.id === result.categoryId);
                const newTag: TagData = {
                    id: result.id,
                    categoryId: result.categoryId,
                    categoryName: currentCat?.name || "Unknown",
                    value: result.fieldValue,
                    status: result.isActive ? "Active" : "Inactive"
                };
                setTags([newTag, ...tags]);
                setSnack({ open: true, msg: "Tag successfully added!", sev: "success" });
            }
            setTagForm({ categoryId: "", value: "", status: "Active" });
            setOpenTagDialog(false);
            setEditingTagId(null);
        } catch (error) {
            console.error("Failed to save tag:", error);
            setSnack({ open: true, msg: "Failed to save tag.", sev: "error" });
        }
    };

    // Lookup Handlers
    const handleOpenLookupDialog = (item?: LookupItem) => {
        if (item) {
            setEditingLookupId(item.id);
            setLookupForm({
                name: item.name,
                code: item.code,
                description: item.description,
                scope: item.scope,
                values: item.values || []
            });
        } else {
            setEditingLookupId(null);
            setLookupForm({
                name: '',
                code: '',
                description: '',
                scope: '',
                values: []
            });
        }
        setOpenLookupDialog(true);
        setShowLookupValueInput(false);
        setNewLookupValue({ order: 1, value: '', description: '' });
    };

    const handleAddLookupValue = () => {
        setLookupForm(prev => ({
            ...prev,
            values: [...prev.values, { order: prev.values.length + 1, value: '', description: '' }]
        }));
    };

    const handleRemoveLookupValue = async (index: number) => {
        const itemToRemove = lookupForm.values[index];
        if (itemToRemove.id) {
            try {
                await masterDataService.deleteLookupValue(itemToRemove.id);
                setSnack({ open: true, msg: "Lookup value deleted!", sev: "success" });
            } catch (err) {
                console.error("Failed to delete lookup value:", err);
                setSnack({ open: true, msg: "Failed to delete value", sev: "error" });
                return; // Don't remove from UI if API failed
            }
        }

        setLookupForm(prev => ({
            ...prev,
            values: prev.values.filter((_, i) => i !== index)
        }));
    };

    const handleSaveLookup = async () => {
        const payload = {
            categoryName: lookupForm.name,
            description: lookupForm.description,
            active: true,
            values: lookupForm.values.map(v => ({
                id: v.id,
                valueName: v.value,
                description: v.description,
                orderNumber: v.order,
                active: true
            }))
        };

        try {
            if (editingLookupId) {
                await masterDataService.updateLookup(editingLookupId, payload);
                setSnack({ open: true, msg: "Lookup updated successfully!", sev: "success" });
            } else {
                await masterDataService.createLookup(payload);
                setSnack({ open: true, msg: "Lookup added successfully!", sev: "success" });
            }
            fetchLookups();
            setOpenLookupDialog(false);
        } catch (err) {
            console.error("Failed to save lookup:", err);
            setSnack({ open: true, msg: "Failed to save lookup", sev: "error" });
        }
    };

    const handleDeleteLookup = async () => {
        if (!deleteLookupId) return;
        try {
            await masterDataService.deleteLookup(deleteLookupId);
            setSnack({ open: true, msg: "Lookup deleted successfully!", sev: "success" });
            fetchLookups();
            setDeleteLookupId(null);
        } catch (err) {
            console.error("Failed to delete lookup:", err);
            setSnack({ open: true, msg: "Failed to delete lookup", sev: "error" });
        }
    };

    const handleDownloadTemplate = async () => {
        try {
            const response = await masterDataService.downloadLookupTemplate();
            const blob = response.data;
            const contentDisposition = response.headers['content-disposition'];
            let fileName = 'lookup_template.xlsx'; // default fallback

            if (contentDisposition) {
                const fileNameMatch = contentDisposition.match(/filename="?(.+)"?/);
                if (fileNameMatch && fileNameMatch[1]) {
                    fileName = fileNameMatch[1].replace(/["']/g, '');
                }
            }

            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (error) {
            console.error("Failed to download template:", error);
            setSnack({ open: true, msg: "Failed to download template", sev: "error" });
        }
    };

    const handleDownloadData = async () => {
        setIsDownloadingData(true);
        try {
            const response = await masterDataService.downloadLookupsData();
            const blob = response.data;
            const contentDisposition = response.headers['content-disposition'];
            let fileName = 'lookups_data.xlsx'; // default fallback

            if (contentDisposition) {
                const fileNameMatch = contentDisposition.match(/filename="?(.+)"?/);
                if (fileNameMatch && fileNameMatch[1]) {
                    fileName = fileNameMatch[1].replace(/["']/g, '');
                }
            }

            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
            link.remove();
            setSnack({ open: true, msg: "Data downloaded successfully!", sev: "success" });
        } catch (error) {
            console.error("Failed to download data:", error);
            setSnack({ open: true, msg: "Failed to download data", sev: "error" });
        } finally {
            setIsDownloadingData(false);
        }
    };

    const handleUpload = async () => {
        if (!selectedFile) return;
        setIsBulkLoading(true);
        try {
            const response = await masterDataService.uploadLookups(selectedFile);
            // Handle id, fileId or uploadId from backend
            const id = response.id || response.fileId || response.uploadId;
            if (id) {
                setFileId(id);
                setUploadStatus('uploaded');
                setSnack({ open: true, msg: "File uploaded successfully!", sev: "success" });
            } else {
                throw new Error("No file ID received from server");
            }
        } catch (error) {
            console.error("Upload failed:", error);
            setUploadStatus('error');
            setSnack({ open: true, msg: "Failed to upload file", sev: "error" });
        } finally {
            setIsBulkLoading(false);
        }
    };

    const handleConfirm = async () => {
        if (!fileId || !selectedFile) return;
        setIsBulkLoading(true);
        try {
            const result = await masterDataService.confirmLookups(fileId, selectedFile);
            setUploadResult(result);
            if (result.errorCount === 0) {
                setUploadStatus('completed');
                setSnack({ open: true, msg: "Data committed to database successfully!", sev: "success" });
                fetchLookups();
            } else {
                setUploadStatus('error');
                setSnack({ open: true, msg: `${result.errorCount} invalid rows found. Download error file to fix.`, sev: "error" });
            }
        } catch (error) {
            console.error("Confirm failed:", error);
            setUploadStatus('error');
            setSnack({ open: true, msg: "Failed to confirm data", sev: "error" });
        } finally {
            setIsBulkLoading(false);
        }
    };

    const handleDownloadErrors = async () => {
        if (!fileId) return;
        try {
            const response = await masterDataService.downloadLookupErrorFile(fileId);
            const blob = response.data;
            const contentDisposition = response.headers['content-disposition'];
            let fileName = 'error_report.xlsx'; // default fallback

            if (contentDisposition) {
                const fileNameMatch = contentDisposition.match(/filename="?(.+)"?/);
                if (fileNameMatch && fileNameMatch[1]) {
                    fileName = fileNameMatch[1].replace(/["']/g, '');
                }
            }

            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (error) {
            console.error("Failed to download error file:", error);
            setSnack({ open: true, msg: "Failed to download error file", sev: "error" });
        }
    };

    const handleAppend = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file || !fileId) return;
        setIsBulkLoading(true);
        try {
            const response = await masterDataService.appendCorrectedLookups(fileId, file);
            const id = response.id || response.fileId || response.uploadId;
            if (id) {
                setFileId(id);
                setSelectedFile(file);
                setUploadStatus('uploaded');
                setUploadResult(null);
                setSnack({ open: true, msg: "Corrected file appended! You can now proceed to confirm.", sev: "success" });
            }
        } catch (error) {
            console.error("Append failed:", error);
            setSnack({ open: true, msg: "Failed to append corrected file", sev: "error" });
        } finally {
            setIsBulkLoading(false);
        }
    };

    const fetchRecentUploads = async () => {
        try {
            const data = await masterDataService.getRecentUploads();
            setRecentUploads(data || []);
        } catch (error) {
            console.error("Failed to fetch recent uploads:", error);
        }
    };

    useEffect(() => {
        if (openBulkDialog) {
            fetchRecentUploads();
        }
    }, [openBulkDialog]);

    return (
        <Box>
            <Box sx={{ borderBottom: 1, borderColor: BORDER, mb: 3 }}>
                <Tabs
                    value={activeTab}
                    onChange={(_, val) => setActiveTab(val)}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{
                        "& .MuiTab-root": {
                            textTransform: "none",
                            fontWeight: 700,
                            minWidth: 120,
                            color: TEXT_SUB,
                        },
                        "& .Mui-selected": {
                            color: `#48CAE4 !important`,
                        },
                        "& .MuiTabs-indicator": {
                            backgroundColor: "#48CAE4",
                            height: 3,
                            borderTopLeftRadius: 3,
                            borderTopRightRadius: 3,
                        }
                    }}
                >
                    <Tab
                        icon={<CategoryIcon sx={{ fontSize: 18, mr: 1, mb: "0 !important", color: "inherit" }} />}
                        iconPosition="start"
                        label=" Categories"
                        value="categories"
                    />
                    <Tab
                        icon={<LabelIcon sx={{ fontSize: 18, mr: 1, mb: "0 !important", color: "inherit" }} />}
                        iconPosition="start"
                        label="Tags"
                        value="tags"
                    />
                    <Tab
                        icon={<FilterAltIcon sx={{ fontSize: 18, mr: 1, mb: "0 !important", color: "inherit" }} />}
                        iconPosition="start"
                        label="Lookups"
                        value="lookups"
                    />
                </Tabs>
            </Box>

            {activeTab === "categories" && (
                <SectionCard
                    title="Tag Categories"
                    subtitle="Manage available master data categories."
                    icon={<CategoryIcon fontSize="small" />}
                    iconColor="#48CAE4"
                    iconBg="#E8F8FB"
                    action={
                        <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
                            <TextField
                                size="small"
                                placeholder="Search by Category Name"
                                value={catSearchQuery}
                                onChange={(e) => setCatSearchQuery(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon sx={{ color: TEXT_SUB, fontSize: 20 }} />
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{
                                    bgcolor: "#FFFFFF", borderRadius: "8px", width: { xs: '100%', sm: 280 },
                                    "& .MuiOutlinedInput-root": { borderRadius: "8px" }
                                }}
                            />
                            <FormControl size="small" sx={{ width: { xs: '100%', sm: 130 } }}>
                                <Select
                                    value={catStatusQuery}
                                    onChange={(e) => setCatStatusQuery(e.target.value as string)}
                                    sx={{ bgcolor: "#FFFFFF", borderRadius: "8px" }}
                                >
                                    <MenuItem value="All">All Status</MenuItem>
                                    <MenuItem value="Active">Active</MenuItem>
                                    <MenuItem value="Inactive">Inactive</MenuItem>
                                </Select>
                            </FormControl>
                            {!readOnly && (
                                <Button
                                    variant="contained"
                                    startIcon={<AddIcon />}
                                    onClick={() => {
                                        setEditingCatId(null);
                                        setCatForm({ name: "", description: "", status: "Active" });
                                        setOpenCatDialog(true);
                                    }}
                                    size="small"
                                    sx={{
                                        borderRadius: '8px',
                                        textTransform: 'none',
                                        fontWeight: 'normal',
                                        bgcolor: '#48CAE4',
                                        color: '#000000',
                                        px: 2,
                                        height: 36,
                                        width: { xs: '100%', sm: 'auto' },

                                        '&:hover': {
                                            bgcolor: '#3ab3cc',
                                        },
                                    }}
                                >
                                    Add Category
                                </Button>
                            )}
                        </Box>
                    }
                >
                    <TableContainer sx={{ borderRadius: "14px", border: `1px solid ${BORDER}` }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ bgcolor: "#F8FAFC" }}>
                                    {["Category Name", "Description", "Status", !readOnly ? "Actions" : ""].filter(h => h !== "").map(h => (
                                        <TableCell key={h} align={h === "Actions" ? "right" : "left"} sx={{ fontWeight: 700, color: TEXT_SUB, fontSize: "0.72rem", textTransform: "uppercase", py: 1.5, px: 2 }}>
                                            {h}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loadingCats ? (
                                    <TableRow>
                                        <TableCell colSpan={4} sx={{ textAlign: "center", py: 5 }}>
                                            <CircularProgress size={32} sx={{ color: PRIMARY }} />
                                            <Typography variant="caption" display="block" sx={{ mt: 1, color: TEXT_SUB }}>Loading categories…</Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : categories.length === 0 ? (
                                    <TableRow><TableCell colSpan={4} sx={{ textAlign: "center", py: 3, color: TEXT_SUB }}>No categories found.</TableCell></TableRow>
                                ) : displayedCats.map(cat => (
                                    <TableRow key={cat.id} sx={{ "&:last-child td": { borderBottom: 0 } }}>
                                        <TableCell sx={{ py: 1.5, px: 2 }}><Typography variant="body2" fontWeight={700} sx={{ color: TEXT_MAIN }}>{cat.name}</Typography></TableCell>
                                        <TableCell sx={{ py: 1.5, px: 2 }}><Typography variant="caption" sx={{ color: TEXT_SUB }}>{cat.description}</Typography></TableCell>
                                        <TableCell sx={{ py: 1.5, px: 2 }}><StatusChip status={cat.status} /></TableCell>
                                        {!readOnly && (
                                            <TableCell sx={{ py: 1.5, px: 2 }} align="right">
                                                <Tooltip title="Edit Category">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => {
                                                            setEditingCatId(cat.id);
                                                            setCatForm({ name: cat.name, description: cat.description, status: cat.status });
                                                            setOpenCatDialog(true);
                                                        }}
                                                        sx={{ color: "#48CAE4", "&:hover": { color: "#3ab3cc", bgcolor: "#E8F8FB" } }}
                                                    >
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Delete Category">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => setDeleteCatId(cat.id)}
                                                        sx={{ color: "#EF4444", "&:hover": { color: "#DC2626", bgcolor: "#FEF2F2" } }}
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        )}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    {categories.length > 0 && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, px: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                                Showing {displayedCats.length} of {categories.length} entries
                            </Typography>
                            <Pagination
                                count={totalCatPages}
                                page={catPage}
                                onChange={(e, value) => setCatPage(value)}
                                variant="outlined"
                                shape="rounded"
                                size="small"
                            />
                        </Box>
                    )}
                </SectionCard>
            )}

            {activeTab === "tags" && (
                <SectionCard
                    title="Tags"
                    subtitle="Manage tag values within categories."
                    icon={<LabelIcon fontSize="small" />}
                    iconColor="#48CAE4"
                    iconBg="#E8F8FB"
                    action={
                        <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
                            <TextField
                                size="small"
                                placeholder="Search By category or tag value"
                                value={tagSearchQuery}
                                onChange={(e) => setTagSearchQuery(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon sx={{ color: TEXT_SUB, fontSize: 20 }} />
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{
                                    bgcolor: "#FFFFFF", borderRadius: "8px", width: { xs: '100%', sm: 280 },
                                    "& .MuiOutlinedInput-root": { borderRadius: "8px" }
                                }}
                            />
                            <FormControl size="small" sx={{ width: { xs: '100%', sm: 130 } }}>
                                <Select
                                    value={tagStatusQuery}
                                    onChange={(e) => setTagStatusQuery(e.target.value as string)}
                                    sx={{ bgcolor: "#FFFFFF", borderRadius: "8px" }}
                                >
                                    <MenuItem value="All">All Status</MenuItem>
                                    <MenuItem value="Active">Active</MenuItem>
                                    <MenuItem value="Inactive">Inactive</MenuItem>
                                </Select>
                            </FormControl>
                            {!readOnly && (
                                <Button
                                    variant="contained"
                                    startIcon={<AddIcon />}
                                    onClick={() => {
                                        setEditingTagId(null);
                                        setTagForm({ categoryId: "", value: "", status: "Active" });
                                        setOpenTagDialog(true);
                                    }}
                                    size="small"
                                    sx={{
                                        borderRadius: '8px',
                                        textTransform: 'none',
                                        fontWeight: 'normal',
                                        bgcolor: '#48CAE4',
                                        color: '#000000',
                                        px: 2,
                                        height: 36,
                                        width: { xs: '100%', sm: 'auto' },

                                        '&:hover': {
                                            bgcolor: '#3ab3cc',
                                        },
                                    }}
                                >
                                    Add Tag
                                </Button>
                            )}
                        </Box>
                    }
                >
                    <TableContainer sx={{ borderRadius: "14px", border: `1px solid ${BORDER}` }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ bgcolor: "#F8FAFC" }}>
                                    {["Category", "Tag Value", "Status", !readOnly ? "Actions" : ""].filter(h => h !== "").map(h => (
                                        <TableCell key={h} align={h === "Actions" ? "right" : "left"} sx={{ fontWeight: 700, color: TEXT_SUB, fontSize: "0.72rem", textTransform: "uppercase", py: 1.5, px: 2 }}>
                                            {h}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loadingTags ? (
                                    <TableRow>
                                        <TableCell colSpan={4} sx={{ textAlign: "center", py: 5 }}>
                                            <CircularProgress size={32} sx={{ color: PRIMARY }} />
                                            <Typography variant="caption" display="block" sx={{ mt: 1, color: TEXT_SUB }}>Loading tags…</Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : tags.length === 0 ? (
                                    <TableRow><TableCell colSpan={4} sx={{ textAlign: "center", py: 3, color: TEXT_SUB }}>No tags found.</TableCell></TableRow>
                                ) : displayedTags.map(tag => (
                                    <TableRow key={tag.id} sx={{ "&:last-child td": { borderBottom: 0 } }}>
                                        <TableCell sx={{ py: 1.5, px: 2 }}><Typography variant="body2" fontWeight={600} sx={{ color: TEXT_MAIN }}>{tag.categoryName}</Typography></TableCell>
                                        <TableCell sx={{ py: 1.5, px: 2 }}><Typography variant="body2" fontWeight={700} sx={{ color: PRIMARY }}>{tag.value}</Typography></TableCell>
                                        <TableCell sx={{ py: 1.5, px: 2 }}><StatusChip status={tag.status} /></TableCell>
                                        {!readOnly && (
                                            <TableCell sx={{ py: 1.5, px: 2 }} align="right">
                                                <Tooltip title="Edit Tag">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => {
                                                            setEditingTagId(tag.id);
                                                            setTagForm({ categoryId: tag.categoryId, value: tag.value, status: tag.status });
                                                            setOpenTagDialog(true);
                                                        }}
                                                        sx={{ color: "#48CAE4", "&:hover": { color: "#3ab3cc", bgcolor: "#E8F8FB" } }}
                                                    >
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Delete Tag">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => setDeleteTagId(tag.id)}
                                                        sx={{ color: "#EF4444", "&:hover": { color: "#DC2626", bgcolor: "#FEF2F2" } }}
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        )}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    {tags.length > 0 && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, px: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                                Showing {displayedTags.length} of {tags.length} entries
                            </Typography>
                            <Pagination
                                count={totalTagPages}
                                page={tagPage}
                                onChange={(e, value) => setTagPage(value)}
                                variant="outlined"
                                shape="rounded"
                                size="small"
                            />
                        </Box>
                    )}
                </SectionCard>
            )}

            {activeTab === "lookups" && (
                <SectionCard
                    title="Lookups"
                    subtitle="Manage systemic lookup values and scopes"
                    icon={<FilterAltIcon fontSize="small" />}
                    iconColor="#48CAE4"
                    iconBg="#E8F8FB"
                    action={
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                            <TextField
                                placeholder="Search by Lookup Name"
                                size="small"
                                value={lookupSearchQuery}
                                onChange={(e) => setLookupSearchQuery(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon sx={{ color: TEXT_SUB, fontSize: 20 }} />
                                        </InputAdornment>
                                    ),
                                    sx: {
                                        borderRadius: '10px',
                                        bgcolor: '#fff',
                                        width: { xs: '100%', sm: 250 },
                                        transition: 'all 0.2s',
                                        '&:hover': {
                                            boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
                                        },
                                        '&.Mui-focused': {
                                            boxShadow: '0 4px 12px rgba(139,92,246,0.1)'
                                        }
                                    }
                                }}
                            />
                            {!readOnly && (
                                <Box sx={{ display: "flex", gap: 2 }}>
                                    <Button
                                        variant="outlined"
                                        startIcon={<BulkActionIcon />}
                                        onClick={() => setOpenBulkDialog(true)}
                                        size="small"
                                        sx={{
                                            borderRadius: "8px", textTransform: "none", fontWeight: 700,
                                            color: PRIMARY, borderColor: PRIMARY,
                                            "&:hover": { bgcolor: "#f0f9ff", borderColor: PRIMARY }, px: 2, height: 36,
                                            whiteSpace: 'nowrap'
                                        }}
                                    >
                                        Bulk Action
                                    </Button>
                                    <Button
                                        variant="contained"
                                        startIcon={<AddIcon />}
                                        onClick={() => handleOpenLookupDialog()}
                                        size="small"
                                        sx={{
                                            borderRadius: '8px',
                                            textTransform: 'none',
                                            fontWeight: 'normal',
                                            bgcolor: '#48CAE4',
                                            color: '#000000',
                                            px: 2,
                                            height: 36,
                                            whiteSpace: 'nowrap',

                                            '&:hover': {
                                                bgcolor: '#3ab3cc',
                                            },
                                        }}
                                    >
                                        Add New Item
                                    </Button>
                                </Box>
                            )}
                        </Box>
                    }
                >
                    <TableContainer sx={{ borderRadius: "14px", border: `1px solid ${BORDER}` }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ bgcolor: "#F8FAFC" }}>
                                    {["Lookup Name", "Description", "Created At", "Updated At", "Status", !readOnly ? "Actions" : ""].filter(h => h !== "").map(h => (
                                        <TableCell key={h} align={h === "Actions" ? "right" : "left"} sx={{ fontWeight: 700, color: TEXT_SUB, fontSize: "0.72rem", py: 1.5, px: 2 }}>
                                            {h}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loadingLookups ? (
                                    <TableRow>
                                        <TableCell colSpan={6} sx={{ textAlign: "center", py: 5 }}>
                                            <CircularProgress size={32} sx={{ color: PRIMARY }} />
                                            <Typography variant="caption" display="block" sx={{ mt: 1, color: TEXT_SUB }}>Loading lookups…</Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : items.length === 0 ? (
                                    <TableRow><TableCell colSpan={6} sx={{ textAlign: "center", py: 3, color: TEXT_SUB }}>No lookups found.</TableCell></TableRow>
                                ) : displayedLookups.map(item => (
                                    <TableRow key={item.id} sx={{ "&:last-child td": { borderBottom: 0 } }}>
                                        <TableCell sx={{ py: 1.5, px: 2 }}><Typography variant="body2" fontWeight={700} sx={{ color: TEXT_MAIN }}>{item.name}</Typography></TableCell>
                                        <TableCell sx={{ py: 1.5, px: 2 }}><Typography variant="body2" sx={{ color: TEXT_SUB }}>{item.description || "—"}</Typography></TableCell>
                                        <TableCell sx={{ py: 1.5, px: 2 }}><Typography variant="caption" sx={{ color: TEXT_SUB }}>{formatToUIDateTimeLong(item.createdAt)}</Typography></TableCell>
                                        <TableCell sx={{ py: 1.5, px: 2 }}><Typography variant="caption" sx={{ color: TEXT_SUB }}>{formatToUIDateTimeLong(item.updatedAt)}</Typography></TableCell>
                                        <TableCell sx={{ py: 1.5, px: 2 }}>
                                            <StatusChip status={item.status} />
                                        </TableCell>
                                        {!readOnly && (
                                            <TableCell sx={{ py: 1.5, px: 2 }} align="right">
                                                <IconButton size="small" onClick={() => handleOpenLookupDialog(item)} sx={{ color: "#48CAE4", "&:hover": { color: "#3ab3cc", bgcolor: "#E8F8FB" } }}>
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                                <IconButton size="small" onClick={() => setDeleteLookupId(item.id)} sx={{ color: "#EF4444", "&:hover": { color: "#DC2626", bgcolor: "#FEF2F2" } }}>
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </TableCell>
                                        )}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    {items.length > rowsPerPage && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, px: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                                Showing {displayedLookups.length} of {items.length} entries
                            </Typography>
                            <Pagination
                                count={totalLookupPages}
                                page={lookupPage}
                                onChange={(e, value) => setLookupPage(value)}
                                variant="outlined"
                                shape="rounded"
                                size="small"
                            />
                        </Box>
                    )}
                </SectionCard>
            )}

            {/* Add/Edit Category Dialog */}
            <Dialog open={openCatDialog} onClose={() => setOpenCatDialog(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: "20px" } }}>
                <DialogTitle sx={{ py: 2.5, px: 3, display: "flex", alignItems: "center", gap: 1.5, background: 'linear-gradient(135deg, #48CAE4 0%, #3ab3cc 100%)', color: "#fff" }}>
                    <Avatar sx={{ bgcolor: "rgba(255,255,255,0.2)", borderRadius: "10px" }}><CategoryIcon /></Avatar>
                    <Typography fontWeight={800} fontSize="1rem">{editingCatId ? "Edit Tag Category" : "Add Tag Category"}</Typography>
                </DialogTitle>
                <DialogContent sx={{ pt: 3, pb: 1 }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <TextField fullWidth required size="small" label="Category Name" value={catForm.name} onChange={e => setCatForm({ ...catForm, name: e.target.value })} sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" }, "& .MuiFormLabel-asterisk": { color: "#d32f2f" } }} />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField fullWidth size="small" multiline minRows={2} label="Description" value={catForm.description} onChange={e => setCatForm({ ...catForm, description: e.target.value })} sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }} />
                        </Grid>
                        <Grid item xs={12}>
                            <FormControl fullWidth required size="small">
                                <InputLabel sx={{ "& .MuiFormLabel-asterisk": { color: "#d32f2f" } }}>Status</InputLabel>
                                <Select value={catForm.status} label="Status" onChange={e => setCatForm({ ...catForm, status: e.target.value as "Active" | "Inactive" })} sx={{ borderRadius: "10px" }}>
                                    <MenuItem value="Active">Active</MenuItem>
                                    <MenuItem value="Inactive">Inactive</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2.5, gap: 1 }}>
                    <Button onClick={() => setOpenCatDialog(false)} sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 600, color: TEXT_SUB }}>Cancel</Button>
                    <Button variant="contained" disabled={!catForm.name || !catForm.status} onClick={handleSaveCategory} sx={{
                        borderRadius: '10px',
                        textTransform: 'none',
                        fontWeight: 700,
                        bgcolor: '#48CAE4',
                        color: '#000000',
                        px: 3,

                        '&:hover': {
                            bgcolor: '#3ab3cc',
                        },
                    }}>
                        Save
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Add/Edit Tag Dialog */}
            <Dialog open={openTagDialog} onClose={() => setOpenTagDialog(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: "20px" } }}>
                <DialogTitle sx={{ py: 2.5, px: 3, display: "flex", alignItems: "center", gap: 1.5, background: 'linear-gradient(135deg, #48CAE4 0%, #3ab3cc 100%)', color: "#fff" }}>
                    <Avatar sx={{ bgcolor: "rgba(255,255,255,0.2)", borderRadius: "10px" }}><LabelIcon /></Avatar>
                    <Typography fontWeight={800} fontSize="1rem">{editingTagId ? "Edit Tag" : "Add Tag"}</Typography>
                </DialogTitle>
                <DialogContent sx={{ pt: 3, pb: 1 }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <FormControl fullWidth required size="small">
                                <InputLabel sx={{ "& .MuiFormLabel-asterisk": { color: "#d32f2f" } }}>Category</InputLabel>
                                <Select value={tagForm.categoryId} label="Category" onChange={e => setTagForm({ ...tagForm, categoryId: e.target.value })} sx={{ borderRadius: "10px" }}>
                                    {categories.map(cat => (
                                        <MenuItem key={cat.id} value={cat.id}>{cat.name}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField fullWidth required size="small" label="Tag Value" value={tagForm.value} onChange={e => setTagForm({ ...tagForm, value: e.target.value })} sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" }, "& .MuiFormLabel-asterisk": { color: "#d32f2f" } }} />
                        </Grid>
                        <Grid item xs={12}>
                            <FormControl fullWidth required size="small">
                                <InputLabel sx={{ "& .MuiFormLabel-asterisk": { color: "#d32f2f" } }}>Status</InputLabel>
                                <Select value={tagForm.status} label="Status" onChange={e => setTagForm({ ...tagForm, status: e.target.value as "Active" | "Inactive" })} sx={{ borderRadius: "10px" }}>
                                    <MenuItem value="Active">Active</MenuItem>
                                    <MenuItem value="Inactive">Inactive</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2.5, gap: 1 }}>
                    <Button onClick={() => setOpenTagDialog(false)} sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 600, color: TEXT_SUB }}>Cancel</Button>
                    <Button variant="contained" disabled={!tagForm.categoryId || !tagForm.value || !tagForm.status} onClick={handleSaveTag} sx={{
                        borderRadius: '10px',
                        textTransform: 'none',
                        fontWeight: 700,
                        bgcolor: '#48CAE4',
                        color: '#000000',
                        px: 3,

                        '&:hover': {
                            bgcolor: '#3ab3cc',
                        },
                    }}>
                        Save
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Category Confirmation Dialog */}
            <Dialog open={!!deleteCatId} onClose={() => setDeleteCatId(null)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: "20px" } }}>
                <DialogTitle sx={{ py: 2.5, px: 3, display: "flex", alignItems: "center", gap: 1.5, color: TEXT_MAIN }}>
                    <Avatar sx={{ bgcolor: "#FEE2E2", color: "#DC2626", borderRadius: "10px" }}><DeleteIcon /></Avatar>
                    <Typography fontWeight={800} fontSize="1rem">Delete Category</Typography>
                </DialogTitle>
                <DialogContent sx={{ px: 3, pb: 1 }}>
                    <Typography variant="body2" color={TEXT_SUB}>
                        Are you sure you want to delete this category?
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2.5, gap: 1 }}>
                    <Button onClick={() => setDeleteCatId(null)} sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 600, color: TEXT_SUB }}>Cancel</Button>
                    <Button variant="contained" onClick={handleDeleteCategory} sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 700, bgcolor: "#DC2626", "&:hover": { bgcolor: "#B91C1C" }, px: 3 }}>
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Tag Confirmation Dialog */}
            <Dialog open={!!deleteTagId} onClose={() => setDeleteTagId(null)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: "20px" } }}>
                <DialogTitle sx={{ py: 2.5, px: 3, display: "flex", alignItems: "center", gap: 1.5, color: TEXT_MAIN }}>
                    <Avatar sx={{ bgcolor: "#FEE2E2", color: "#DC2626", borderRadius: "10px" }}><DeleteIcon /></Avatar>
                    <Typography fontWeight={800} fontSize="1rem">Delete Tag</Typography>
                </DialogTitle>
                <DialogContent sx={{ px: 3, pb: 1 }}>
                    <Typography variant="body2" color={TEXT_SUB}>
                        Are you sure you want to delete this tag?
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2.5, gap: 1 }}>
                    <Button onClick={() => setDeleteTagId(null)} sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 600, color: TEXT_SUB }}>Cancel</Button>
                    <Button variant="contained" onClick={handleDeleteTag} sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 700, bgcolor: "#DC2626", "&:hover": { bgcolor: "#B91C1C" }, px: 3 }}>
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={openLookupDialog} onClose={() => setOpenLookupDialog(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: "12px", p: 1 } }}>
                <DialogTitle sx={{ fontWeight: 800, fontSize: "1.25rem", color: TEXT_MAIN, pb: 1 }}>
                    {editingLookupId ? "Edit Lookup" : "Add New Lookup"}
                </DialogTitle>
                <DialogContent sx={{ pt: 2 }}>
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="caption" fontWeight={700} sx={{ color: TEXT_SUB, textTransform: 'uppercase', mb: 1, display: 'block' }}>
                            Lookup Name <span style={{ color: '#d32f2f' }}>*</span>
                        </Typography>
                        <TextField
                            fullWidth
                            placeholder="Add lookup name"
                            size="small"
                            value={lookupForm.name}
                            onChange={e => setLookupForm({ ...lookupForm, name: e.target.value })}
                            sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px", bgcolor: "#F8FAFC" } }}
                        />
                    </Box>

                    <Box sx={{ mb: 3 }}>
                        <Typography variant="caption" fontWeight={700} sx={{ color: TEXT_SUB, textTransform: 'uppercase', mb: 1, display: 'block' }}>
                            Description
                        </Typography>
                        <TextField
                            fullWidth
                            multiline
                            rows={3}
                            placeholder="Description of the lookup"
                            value={lookupForm.description}
                            onChange={e => setLookupForm({ ...lookupForm, description: e.target.value })}
                            sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px", bgcolor: "#F8FAFC" } }}
                        />
                    </Box>

                    <Box sx={{ mt: 4 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="caption" fontWeight={700} sx={{ color: TEXT_SUB, textTransform: 'uppercase' }}>
                                Lookup Values
                            </Typography>
                            <Button
                                size="small"
                                startIcon={<AddIcon sx={{ fontSize: '1rem !important' }} />}
                                onClick={handleAddLookupValue}
                                sx={{
                                    textTransform: 'none',
                                    fontWeight: 700,
                                    color: PRIMARY,
                                    fontSize: '0.8rem',
                                    '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' }
                                }}
                            >
                                Add Value
                            </Button>
                        </Box>

                        {lookupForm.values.length > 0 && (
                            <>
                                <Grid container sx={{ mb: 1, px: 2 }}>
                                    <Grid item xs={1.5}><Typography variant="caption" fontWeight={800} sx={{ color: TEXT_SUB, fontSize: '0.65rem' }}>ORDER</Typography></Grid>
                                    <Grid item xs={4.5}><Typography variant="caption" fontWeight={800} sx={{ color: TEXT_SUB, fontSize: '0.65rem' }}>VALUE</Typography></Grid>
                                    <Grid item xs={5.5}><Typography variant="caption" fontWeight={800} sx={{ color: TEXT_SUB, fontSize: '0.65rem' }}>DESCRIPTION</Typography></Grid>
                                </Grid>

                                <Stack spacing={1.5}>
                                    {lookupForm.values.map((val, index) => (
                                        <Box key={index} sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                                            <TextField
                                                size="small"
                                                type="number"
                                                value={val.order}
                                                onChange={e => {
                                                    const newVals = [...lookupForm.values];
                                                    newVals[index].order = parseInt(e.target.value) || 0;
                                                    setLookupForm({ ...lookupForm, values: newVals });
                                                }}
                                                sx={{ width: '12%', "& .MuiOutlinedInput-root": { borderRadius: "8px", bgcolor: "#fff", fontWeight: 700, textAlign: 'center' } }}
                                            />
                                            <TextField
                                                size="small"
                                                placeholder="Display Value"
                                                value={val.value}
                                                onChange={e => {
                                                    const newVals = [...lookupForm.values];
                                                    newVals[index].value = e.target.value;
                                                    setLookupForm({ ...lookupForm, values: newVals });
                                                }}
                                                sx={{ flex: 1, "& .MuiOutlinedInput-root": { borderRadius: "8px", bgcolor: "#fff" } }}
                                            />
                                            <TextField
                                                size="small"
                                                placeholder="Description"
                                                value={val.description}
                                                onChange={e => {
                                                    const newVals = [...lookupForm.values];
                                                    newVals[index].description = e.target.value;
                                                    setLookupForm({ ...lookupForm, values: newVals });
                                                }}
                                                sx={{ flex: 2, "& .MuiOutlinedInput-root": { borderRadius: "8px", bgcolor: "#fff" } }}
                                            />
                                            <IconButton size="small" onClick={() => handleRemoveLookupValue(index)} sx={{ color: '#F87171' }}>
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Box>
                                    ))}
                                </Stack>
                            </>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 3, gap: 1.5 }}>
                    <Button
                        onClick={() => setOpenLookupDialog(false)}
                        variant="outlined"
                        sx={{
                            borderRadius: "8px",
                            textTransform: "none",
                            fontWeight: 700,
                            color: TEXT_SUB,
                            borderColor: BORDER,
                            px: 3
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        disabled={!lookupForm.name}
                        onClick={handleSaveLookup}
                        sx={{
                            borderRadius: '8px',
                            textTransform: 'none',
                            fontWeight: 700,
                            bgcolor: '#48CAE4',
                            color: '#000000',
                            px: 4,

                            '&:hover': {
                                bgcolor: '#3ab3cc',
                            },
                        }}
                    >
                        {editingLookupId ? "Save Changes" : "Add Lookup"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Lookup Confirmation */}
            <Dialog open={!!deleteLookupId} onClose={() => setDeleteLookupId(null)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: "20px" } }}>
                <DialogTitle sx={{ py: 2.5, px: 3, display: "flex", alignItems: "center", gap: 1.5, color: TEXT_MAIN }}>
                    <Avatar sx={{ bgcolor: "#FEE2E2", color: "#DC2626", borderRadius: "10px" }}><DeleteIcon /></Avatar>
                    <Typography fontWeight={800} fontSize="1rem">Delete Lookup</Typography>
                </DialogTitle>
                <DialogContent sx={{ px: 3, pb: 1 }}>
                    <Typography variant="body2" color={TEXT_SUB}>
                        Are you sure you want to delete this lookup item?
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2.5, gap: 1 }}>
                    <Button onClick={() => setDeleteLookupId(null)} sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 600, color: TEXT_SUB }}>Cancel</Button>
                    <Button variant="contained" onClick={handleDeleteLookup} sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 700, bgcolor: "#DC2626", "&:hover": { bgcolor: "#B91C1C" }, px: 3 }}>
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Bulk Actions Dialog */}
            <Dialog
                open={openBulkDialog}
                onClose={() => setOpenBulkDialog(false)}
                maxWidth="md"
                fullWidth
                PaperProps={{ sx: { borderRadius: "20px", overflow: "hidden" } }}
            >
                <DialogTitle sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    py: 2.5,
                    px: 3,
                    borderBottom: `1px solid ${BORDER}`
                }}>
                    <Typography fontWeight={800} fontSize="1.2rem" sx={{ color: TEXT_MAIN }}>
                        Bulk Actions - Lookup
                    </Typography>
                    <IconButton onClick={() => setOpenBulkDialog(false)} size="small" sx={{ color: TEXT_SUB }}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ p: 0 }}>
                    <Box sx={{ borderBottom: 1, borderColor: BORDER, px: 3 }}>
                        <Tabs
                            value={bulkTab}
                            onChange={(_, val) => setBulkTab(val)}
                            sx={{
                                "& .MuiTab-root": {
                                    textTransform: "none",
                                    fontWeight: 700,
                                    minWidth: 100,
                                    color: TEXT_SUB,
                                    py: 2
                                },
                                "& .Mui-selected": {
                                    color: `${PRIMARY} !important`,
                                },
                                "& .MuiTabs-indicator": {
                                    backgroundColor: PRIMARY,
                                    height: 3,
                                }
                            }}
                        >
                            <Tab label="Bulk Upload" />
                            <Tab label="Download Data" />
                        </Tabs>
                    </Box>

                    <Box sx={{ p: 3 }}>
                        {bulkTab === 0 ? (
                            <Stack spacing={3}>
                                {/* Instructions */}
                                <Box sx={{
                                    bgcolor: "#F0F7FF",
                                    borderRadius: "12px",
                                    p: 2.5,
                                    border: "1px solid #E0F2FE"
                                }}>
                                    <Typography fontWeight={800} sx={{ color: "#1E40AF", mb: 1.5, fontSize: "0.9rem" }}>
                                        Instructions
                                    </Typography>
                                    <ul style={{ margin: 0, paddingLeft: "20px", color: "#1E40AF", fontSize: "0.85rem" }}>
                                        <li style={{ marginBottom: "8px" }}>Download the template to see required format.</li>
                                        <li style={{ marginBottom: "8px" }}>Ensure all required fields are filled.</li>
                                        <li style={{ marginBottom: "12px" }}>Maximum file size: 5MB.</li>
                                    </ul>
                                    <Typography
                                        variant="body2"
                                        onClick={handleDownloadTemplate}
                                        sx={{
                                            color: PRIMARY,
                                            fontWeight: 700,
                                            textDecoration: "underline",
                                            cursor: "pointer",
                                            display: "inline-block",
                                            mt: 0.5
                                        }}
                                    >
                                        Download Template
                                    </Typography>
                                </Box>

                                {/* File Selection */}
                                <Box>
                                    <Typography variant="caption" fontWeight={800} sx={{ color: TEXT_SUB, textTransform: "uppercase", display: "block", mb: 1.5 }}>
                                        Select File
                                    </Typography>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
                                        <Button
                                            component="label"
                                            variant="contained"
                                            sx={{
                                                bgcolor: "#6366F1",
                                                borderRadius: "10px",
                                                textTransform: "none",
                                                fontWeight: 700,
                                                px: 3,
                                                "&:hover": { bgcolor: "#4F46E5" }
                                            }}
                                        >
                                            Choose File
                                            <input
                                                type="file"
                                                hidden
                                                accept=".csv"
                                                onChange={(e) => {
                                                    const file = e.target.files?.[0] || null;
                                                    setSelectedFile(file);
                                                    setUploadStatus('idle');
                                                    setFileId(null);
                                                    setUploadResult(null);
                                                }}
                                            />
                                        </Button>
                                        <Typography variant="body2" sx={{ color: TEXT_SUB, fontStyle: selectedFile ? "normal" : "italic" }}>
                                            {selectedFile ? selectedFile.name : "No file chosen"}
                                        </Typography>
                                        <Box sx={{ flexGrow: 1 }} />
                                        <Button
                                            variant="contained"
                                            disabled={!selectedFile || isBulkLoading || uploadStatus !== 'idle'}
                                            onClick={handleUpload}
                                            sx={{
                                                bgcolor: PRIMARY,
                                                color: "#fff",
                                                borderRadius: "10px",
                                                textTransform: "none",
                                                fontWeight: 700,
                                                px: 4,
                                                "&:hover": { bgcolor: "#005F8E" },
                                                "&.Mui-disabled": { bgcolor: "#E2E8F0", color: "#94A3B8" }
                                            }}
                                        >
                                            {isBulkLoading && uploadStatus === 'idle' ? <CircularProgress size={20} sx={{ color: "#fff", mr: 1 }} /> : null}
                                            Upload
                                        </Button>
                                    </Box>

                                    {/* Action Flow UI (Visible after upload) */}
                                    {fileId && (
                                        <Box sx={{
                                            p: 2.5,
                                            borderRadius: "16px",
                                            bgcolor: uploadStatus === 'completed' ? "#F0FDF4" : "#F8FAFC",
                                            border: `1px solid ${uploadStatus === 'completed' ? "#BBF7D0" : BORDER}`,
                                            mt: 2
                                        }}>
                                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 2 }}>
                                                <Box>
                                                    <Typography variant="body2" fontWeight={800} sx={{ color: TEXT_MAIN, display: "flex", alignItems: "center", gap: 1 }}>
                                                        File Status:
                                                        {uploadStatus === 'uploaded' && <Chip label="Uploaded" size="small" sx={{ bgcolor: "#DBEAFE", color: "#2563EB", fontWeight: 700, borderRadius: "6px" }} />}
                                                        {uploadStatus === 'completed' && <Chip label="Committed" size="small" sx={{ bgcolor: "#DCFCE7", color: "#16A34A", fontWeight: 700, borderRadius: "6px" }} />}
                                                        {uploadStatus === 'error' && uploadResult && uploadResult.errorCount > 0 && (
                                                            <Button
                                                                size="small"
                                                                onClick={handleDownloadErrors}
                                                                startIcon={<InactiveIcon sx={{ fontSize: "14px !important" }} />}
                                                                sx={{
                                                                    bgcolor: "#FEE2E2",
                                                                    color: "#DC2626",
                                                                    fontWeight: 700,
                                                                    borderRadius: "6px",
                                                                    textTransform: "none",
                                                                    fontSize: "0.75rem",
                                                                    py: 0.25,
                                                                    px: 1,
                                                                    "&:hover": { bgcolor: "#FECACA" }
                                                                }}
                                                            >
                                                                Download Error File
                                                            </Button>
                                                        )}
                                                    </Typography>
                                                    {uploadResult && (
                                                        <Typography variant="caption" sx={{ color: TEXT_SUB }}>
                                                            Rows: {uploadResult.successCount} Valid / {uploadResult.errorCount} Invalid (Total: {uploadResult.totalRows})
                                                        </Typography>
                                                    )}
                                                </Box>
                                                <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
                                                    {uploadStatus === 'uploaded' && (
                                                        <Button
                                                            variant="contained"
                                                            size="small"
                                                            disabled={isBulkLoading}
                                                            onClick={handleConfirm}
                                                            startIcon={isBulkLoading ? <CircularProgress size={16} /> : <ActiveIcon sx={{ fontSize: "16px !important" }} />}
                                                            sx={{ bgcolor: "#16A34A", "&:hover": { bgcolor: "#15803D" }, borderRadius: "8px", textTransform: "none", fontWeight: 700, px: 2 }}
                                                        >
                                                            Proceed & Confirm
                                                        </Button>
                                                    )}
                                                    {uploadStatus === 'error' && uploadResult && uploadResult.errorCount > 0 && (
                                                        <>
                                                            <input
                                                                type="file"
                                                                hidden
                                                                ref={appendInputRef}
                                                                accept=".csv,.xlsx"
                                                                onChange={handleAppend}
                                                            />
                                                            <Button
                                                                variant="outlined"
                                                                size="small"
                                                                disabled={isBulkLoading}
                                                                onClick={() => appendInputRef.current?.click()}
                                                                startIcon={isBulkLoading ? <CircularProgress size={16} /> : <BulkActionIcon sx={{ fontSize: "16px !important" }} />}
                                                                sx={{ color: "#6366F1", borderColor: "#6366F1", "&:hover": { borderColor: "#4F46E5", bgcolor: "#F5F3FF" }, borderRadius: "8px", textTransform: "none", fontWeight: 700, px: 2 }}
                                                            >
                                                                Append Corrected
                                                            </Button>
                                                        </>
                                                    )}
                                                </Box>
                                            </Box>
                                        </Box>
                                    )}
                                </Box>

                                {/* Recent Uploads */}
                                <Box>
                                    <Typography fontWeight={800} sx={{ color: TEXT_MAIN, mb: 2, fontSize: "1rem" }}>
                                        Recent Uploads
                                    </Typography>
                                    <TableContainer sx={{ borderRadius: "12px", border: `1px solid ${BORDER}` }}>
                                        <Table size="small">
                                            <TableHead sx={{ bgcolor: "#F8FAFC" }}>
                                                <TableRow>
                                                    {["Date", "File", "Created By", "Status"].map(h => (
                                                        <TableCell key={h} sx={{ fontWeight: 700, color: TEXT_SUB, fontSize: "0.72rem", py: 1.5 }}>
                                                            {h}
                                                        </TableCell>
                                                    ))}
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {recentUploads.length > 0 ? (
                                                    recentUploads.map((upload, idx) => (
                                                        <TableRow key={upload.id || idx}>
                                                            <TableCell sx={{ color: TEXT_SUB }}>
                                                                {upload.createdAt ? new Date(upload.createdAt).toLocaleDateString() : '—'}
                                                            </TableCell>
                                                            <TableCell sx={{ color: TEXT_MAIN, fontWeight: 500 }}>
                                                                {upload.fileName || '—'}
                                                            </TableCell>
                                                            <TableCell sx={{ color: TEXT_SUB }}>
                                                                {upload.createdByName || upload.createdBy || 'System'}
                                                            </TableCell>
                                                            <TableCell>
                                                                <Chip
                                                                    label={upload.status || 'Unknown'}
                                                                    size="small"
                                                                    sx={{
                                                                        bgcolor: ['COMPLETED', 'SUCCESS'].includes(upload.status) ? "#DCFCE7" : "#FEE2E2",
                                                                        color: ['COMPLETED', 'SUCCESS'].includes(upload.status) ? "#16A34A" : "#DC2626",
                                                                        fontWeight: 600,
                                                                        fontSize: "11px"
                                                                    }}
                                                                />
                                                            </TableCell>
                                                        </TableRow>
                                                    ))
                                                ) : (
                                                    <TableRow>
                                                        <TableCell colSpan={4} sx={{ textAlign: "center", py: 4, color: TEXT_SUB }}>
                                                            No recent uploads found.
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Box>
                            </Stack>
                        ) : (
                            <Box sx={{ py: 6, px: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: "center", bgcolor: '#F8FAFC', borderRadius: '16px', border: `1px solid ${BORDER}` }}>
                                <Box sx={{ width: 64, height: 64, borderRadius: '50%', bgcolor: '#E0F2FE', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 3 }}>
                                    <DownloadIcon sx={{ fontSize: 32, color: PRIMARY }} />
                                </Box>
                                <Typography variant="h6" fontWeight={800} sx={{ color: TEXT_MAIN, mb: 1 }}>
                                    Export Lookup Data
                                </Typography>
                                <Typography variant="body2" sx={{ color: TEXT_SUB, mb: 4, maxWidth: 400 }}>
                                    Download all existing lookups and their corresponding values in a structured Excel format. This file can be used for reporting, audits, or as a reference for bulk updates.
                                </Typography>
                                <Button
                                    variant="contained"
                                    onClick={handleDownloadData}
                                    disabled={isDownloadingData}
                                    startIcon={isDownloadingData ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : <DownloadIcon />}
                                    sx={{
                                        bgcolor: PRIMARY,
                                        color: '#fff',
                                        fontWeight: 700,
                                        px: 4,
                                        py: 1.2,
                                        borderRadius: '10px',
                                        textTransform: 'none',
                                        boxShadow: '0 4px 12px rgba(0, 119, 182, 0.2)',
                                        '&:hover': { bgcolor: '#005F8E' },
                                        '&.Mui-disabled': { bgcolor: '#E2E8F0', color: '#94A3B8' }
                                    }}
                                >
                                    {isDownloadingData ? 'Downloading...' : 'Download Full Data'}
                                </Button>
                            </Box>
                        )}
                    </Box>
                </DialogContent>
            </Dialog>

            {/* Snackbar */}
            <Snackbar
                open={snack.open}
                autoHideDuration={4000}
                onClose={() => setSnack({ ...snack, open: false })}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
            >
                <Alert
                    onClose={() => setSnack({ ...snack, open: false })}
                    severity={snack.sev as "success" | "error"}
                    sx={{ width: "100%", borderRadius: "10px", fontWeight: 600 }}
                >
                    {snack.msg}
                </Alert>
            </Snackbar>

        </Box>
    );
};

export default MasterDataTab;
